import type {
  CmmdForgeAccountPreferences,
  FirstAccountSetupState,
  UpdateCmmdForgeAccountPreferencesParams,
} from "@cmmd-forge/contracts";
import { useQuery, type QueryClient } from "@tanstack/react-query";

import { createCmmdApiClient } from "../environments/cmmdApi";

export const CMMD_FORGE_ACCOUNT_PREFERENCES_QUERY_KEY = [
  "cmmd",
  "forge-account-preferences",
] as const;

export function useCmmdForgeAccountPreferences(enabled: boolean) {
  return useQuery({
    queryKey: CMMD_FORGE_ACCOUNT_PREFERENCES_QUERY_KEY,
    queryFn: () => createCmmdApiClient().getForgeAccountPreferences(),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * CMMD's published preferences enum does not include `environment` yet.
 * Keep that step in Forge memory only. Reload still re-enters Environment
 * when the catalog has no Environment id.
 */
export function publishedCmmdSetupPreferences(state: {
  readonly status: FirstAccountSetupState["status"];
  readonly activeStepId: FirstAccountSetupState["activeStepId"] | "environment";
}): UpdateCmmdForgeAccountPreferencesParams | null {
  const activeStepId = state.activeStepId;
  if (activeStepId === "environment") {
    return null;
  }
  return {
    firstAccountSetup: {
      status: state.status,
      activeStepId,
    },
  };
}

export async function updateCmmdForgeAccountPreferences(input: {
  readonly queryClient: QueryClient;
  readonly preferences: UpdateCmmdForgeAccountPreferencesParams;
}): Promise<CmmdForgeAccountPreferences> {
  const preferences = await createCmmdApiClient().updateForgeAccountPreferences(input.preferences);
  input.queryClient.setQueryData(CMMD_FORGE_ACCOUNT_PREFERENCES_QUERY_KEY, preferences);
  return preferences;
}

/**
 * A transient failure (a dropped connection, a cold Sprite, a 5xx) must not
 * give up on the very first try, but it also must not retry forever and hold
 * up the queue behind it. Two retries, backing off, are the bound.
 */
const PERSIST_RETRY_DELAYS_MS = [500, 2000] as const;

/**
 * PATCH the workspace preference, retrying failures after 500 ms and 2000 ms.
 * Resolve true on success or false after all three attempts fail.
 */
async function sendPreferenceWithRetry(organizationId: number): Promise<boolean> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      await createCmmdApiClient().updateForgeAccountPreferences({
        lastActiveOrganizationId: organizationId,
      });
      return true;
    } catch {
      if (attempt >= PERSIST_RETRY_DELAYS_MS.length) return false;
      await new Promise<void>((resolve) => setTimeout(resolve, PERSIST_RETRY_DELAYS_MS[attempt]));
    }
  }
}

let _persistInFlight: Promise<void> | null = null;
let _persistQueued: {
  readonly organizationId: number;
  readonly resolve: (success: boolean) => void;
} | null = null;

/**
 * Persist one workspace, report its result, then send the latest queued value.
 * Keep the queue busy until the current write and its retries settle.
 */
function runPersistQueue(organizationId: number, resolve: (success: boolean) => void): void {
  _persistInFlight = sendPreferenceWithRetry(organizationId)
    .then(resolve)
    .then(() => {
      _persistInFlight = null;
      const queued = _persistQueued;
      if (queued !== null) {
        _persistQueued = null;
        runPersistQueue(queued.organizationId, queued.resolve);
      }
    });
}

/**
 * Single-flight persist: only one PATCH sequence (including its own retries)
 * is in flight at a time. Rapid workspace switches queue only the latest id.
 * When the current sequence settles, it sends the queued value (if any) so
 * the most recent switch always wins.
 *
 * Resolves `true` only once a PATCH for exactly this `organizationId` has
 * actually landed. A call superseded by a newer one before it ever sends
 * resolves `false`, the same as one that sent and failed every retry: in
 * both cases nothing was persisted for that id, and the caller
 * (`authenticatedAppSurface.workspaceSideEffects.ts`) uses that to decide
 * whether it can treat the id as durably recorded.
 */
export function persistLastActiveOrganizationId(organizationId: number): Promise<boolean> {
  if (_persistInFlight !== null) {
    return new Promise<boolean>((resolve) => {
      // A sequence is already in flight. Only the latest id matters: an
      // earlier queued call this supersedes never gets sent, so it resolves
      // `false` now rather than waiting on a PATCH that will never carry it.
      _persistQueued?.resolve(false);
      _persistQueued = { organizationId, resolve };
    });
  }
  return new Promise<boolean>((resolve) => {
    runPersistQueue(organizationId, resolve);
  });
}

/** Test-only: clears the module-level single-flight/queue state between tests. */
export function __resetPersistQueueForTests(): void {
  _persistInFlight = null;
  _persistQueued = null;
}
